import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, } from "react";
import { lockBodyScroll } from "./scroll-lock.js";
const TAB_STOP_SELECTOR = "[data-smm-auth-tab-stop='true']:not(:disabled)";
export const getFocusableElements = (container) => {
    return Array.from(container.querySelectorAll(TAB_STOP_SELECTOR)).filter((element) => element.tabIndex >= 0 && element.offsetParent !== null);
};
export const trapFocusInElement = (container, activeElement, shiftKey) => {
    var _a, _b, _c;
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length === 0)
        return false;
    if (!activeElement || !container.contains(activeElement)) {
        (_a = focusableElements[0]) === null || _a === void 0 ? void 0 : _a.focus();
        return true;
    }
    const currentIndex = focusableElements.indexOf(activeElement);
    if (currentIndex === -1) {
        (_b = focusableElements[0]) === null || _b === void 0 ? void 0 : _b.focus();
        return true;
    }
    const nextIndex = shiftKey
        ? (currentIndex - 1 + focusableElements.length) % focusableElements.length
        : (currentIndex + 1) % focusableElements.length;
    (_c = focusableElements[nextIndex]) === null || _c === void 0 ? void 0 : _c.focus();
    return true;
};
export const shouldLockBodyScroll = (mask) => mask !== null && mask !== void 0 ? mask : true;
export const AuthWidgetShell = ({ mode, mask, maskClosable, children, onClose }) => {
    const [isClosing, setIsClosing] = useState(false);
    const modalRef = useRef(null);
    const closeReasonRef = useRef("cancel");
    const closeTimerRef = useRef(null);
    const requestCloseRef = useRef(() => undefined);
    const usesDrawerAnimation = mode === "drawer" && window.innerWidth <= 768;
    const closeAnimationName = usesDrawerAnimation ? "smm-auth-drawer-exit" : "smm-auth-modal-exit";
    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);
    const finishClose = useCallback(() => {
        clearCloseTimer();
        onClose(closeReasonRef.current);
    }, [clearCloseTimer, onClose]);
    const requestClose = useCallback((reason) => {
        if (isClosing)
            return;
        closeReasonRef.current = reason;
        setIsClosing(true);
        closeTimerRef.current = window.setTimeout(finishClose, 280);
    }, [finishClose, isClosing]);
    useEffect(() => {
        requestCloseRef.current = requestClose;
    }, [requestClose]);
    useEffect(() => {
        const restoreBodyScroll = shouldLockBodyScroll(mask) ? lockBodyScroll() : undefined;
        const onKey = (event) => {
            if (event.key === "Escape")
                requestCloseRef.current("cancel");
        };
        document.addEventListener("keydown", onKey);
        return () => {
            restoreBodyScroll === null || restoreBodyScroll === void 0 ? void 0 : restoreBodyScroll();
            document.removeEventListener("keydown", onKey);
        };
    }, [mask]);
    useEffect(() => clearCloseTimer, [clearCloseTimer]);
    const handleAnimationEnd = useCallback((event) => {
        if (!isClosing || event.animationName !== closeAnimationName)
            return;
        finishClose();
    }, [closeAnimationName, finishClose, isClosing]);
    const handleKeyDown = useCallback((event) => {
        if (event.defaultPrevented || event.key !== "Tab")
            return;
        const modal = modalRef.current;
        if (!modal)
            return;
        if (trapFocusInElement(modal, event.target, event.shiftKey)) {
            event.preventDefault();
        }
    }, []);
    return (_jsxs("div", { className: ["smm-auth-shell open", isClosing && "closing"].filter(Boolean).join(" "), role: "presentation", onAnimationEnd: handleAnimationEnd, onKeyDown: handleKeyDown, children: [mask && _jsx("div", { className: "smm-auth-mask", onClick: maskClosable ? () => requestClose("cancel") : undefined }), _jsx("div", { ref: modalRef, className: "modalWrapper", children: children(requestClose) })] }));
};
//# sourceMappingURL=AuthWidgetShell.js.map